/**
 * tableSearch.js — بحث نصي متسامح داخل جداول البيانات
 *
 * يطبّع النص العربي قبل المقارنة حتى لا يفشل البحث بسبب فروق الكتابة:
 * فمن يكتب "احمد" يجد "أحمد"، ومن يكتب "فاطمه" يجد "فاطمة".
 */

const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const EXTENDED_ARABIC_INDIC_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/**
 * يوحّد صورة النص: يزيل التشكيل والتطويل، ويوحّد الهمزات والتاء المربوطة
 * والألف المقصورة، ويحوّل الأرقام العربية إلى لاتينية.
 * @param {*} value
 * @returns {string}
 */
export function normalizeText(value) {
  if (value === null || value === undefined) return '';

  let text = String(value);

  // الأرقام العربية والفارسية → لاتينية
  text = text.replace(/[٠-٩]/g, (d) => String(ARABIC_INDIC_DIGITS.indexOf(d)));
  text = text.replace(/[۰-۹]/g, (d) => String(EXTENDED_ARABIC_INDIC_DIGITS.indexOf(d)));

  return text
    .replace(/[ً-ْٰ]/g, '') // التشكيل
    .replace(/ـ/g, '')                // التطويل ـ
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/[ىي]/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * يجمع القيم القابلة للبحث من صف. إن لم تُحدَّد الحقول، تُمسح كل القيم
 * النصية والرقمية في الكائن (بما فيها المتداخلة بمستوى واحد).
 * @param {object} row
 * @param {string[]} [fields]
 * @returns {string}
 */
function rowToSearchableText(row, fields) {
  if (!row || typeof row !== 'object') return normalizeText(row);

  const parts = [];
  const keys = fields && fields.length ? fields : Object.keys(row);

  for (const key of keys) {
    const value = row[key];
    if (value === null || value === undefined) continue;
    const type = typeof value;
    if (type === 'string' || type === 'number') {
      parts.push(String(value));
    } else if (type === 'object' && !fields) {
      // كائن متداخل: خذ قيمه المباشرة فقط لتفادي المسح العميق المكلف
      for (const nested of Object.values(value)) {
        const nestedType = typeof nested;
        if (nestedType === 'string' || nestedType === 'number') parts.push(String(nested));
      }
    }
  }

  return normalizeText(parts.join(' '));
}

/**
 * يفلتر الصفوف بعبارة البحث. كل كلمة في العبارة يجب أن تظهر في الصف
 * (بحث AND) حتى يمكن تضييق النتائج بكتابة كلمتين، مثل "أحمد الرياض".
 *
 * @param {object[]} rows الصفوف
 * @param {string} query عبارة البحث
 * @param {string[]} [fields] حقول محددة للبحث فيها (الافتراضي: كل الحقول)
 * @returns {object[]}
 */
export function filterRows(rows, query, fields) {
  // تُرجع مصفوفة دائماً: استجابة API بشكل غير متوقع تعني جدولاً فارغاً
  // بدلاً من انهيار الصفحة عند استدعاء .map عليها
  if (!Array.isArray(rows)) return [];

  const needle = normalizeText(query);
  if (!needle) return rows;

  const terms = needle.split(' ').filter(Boolean);

  return rows.filter((row) => {
    const haystack = rowToSearchableText(row, fields);
    return terms.every((term) => haystack.includes(term));
  });
}

export default filterRows;
